import React, {Component} from 'react'
import {Link as ReactRouterLink} from 'react-router-dom'
import {withTranslation} from 'react-i18next'
import {connect} from 'react-redux'
import {bindActionCreators} from 'redux'
import {actions as authenticationActions} from '../../../actions/authentication'
import {actions as employeeActions} from '../../../actions/employee'
import {actions as staticContentActions} from '../../../actions/static_content'
import {actions as configurationActions} from '../../../actions/configuration'
import {history} from '../../../store'

import ChangeLanguageDialog from './ChangeLanguageDialog'
import Logo from './Logo';
import ImpersonateUserDialog from './ImpersonateUserDialog'
import {NavigationTo} from "../common";

import employeeService from '../../../services/Employee.service'
import {UserRoles} from '../../../constants'
import { setFocusToDashboard } from '../../../components/v2/common/common'

import {Col, Spacer, Row, View} from '@amzn/stencil-react-components/layout'
import {
  PageHeader,
  PageHeaderDropdown,
  PageHeaderDropdownButton,
  PageHeaderLink,
} from '@amzn/stencil-react-components/page'
import {
  IconGlobe,
  IconPadlock
} from '@amzn/stencil-react-components/icons'
import {
  Chip,
  ChipSize,
  ChipBackgroundColor
} from '@amzn/stencil-react-components/chip'
import {Avatar, AvatarColorScheme, AvatarText} from "@amzn/stencil-react-components/avatar"
import {WithBreakpoints} from "@amzn/stencil-react-components/responsive"
import {Text} from "@amzn/stencil-react-components/text";

const PageHeaderRouterLink = PageHeaderLink.withComponent(ReactRouterLink);
const NO_EXISTING_USER_NAME = "NO_USER_NAME";

class UserMenu extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isChangeLanguageDialogVisible: false,
      isImpersonateUserDialogVisible: false
    };
  }

  setChangeLanguageDialogVisble = (value) => {
    this.setState({
      isChangeLanguageDialogVisible: value
    });
  }

  setImpersonateUserDialogVisible = (value) => {
    this.setState({
      isImpersonateUserDialogVisible: value
    });
  }

  stopImpersonation = () => {
    this.props.actions.authentication.removeImpersonate((employeeId) => {
      this.props.actions.employee.unload();
      this.props.actions.configuration.unload();
      this.props.actions.static_content.unload();

      history.push(employeeId === '' ? NavigationTo.Impersonate : NavigationTo.AtGlance);
    });
  }

  isImpersonated = () => {
    return employeeService.hasRole(this.props.employee.roles, UserRoles.IMPERSONATED);
  }

  canImpersonate = () => {
    return employeeService.hasRole(this.props.employee.roles, UserRoles.IMPERSONATE) && !this.isImpersonated();
  }

  createImpersonatingIndicator = (t) => {
    if (this.isImpersonated()) {
      return (
        <Chip
          size={ChipSize.Small}
          backgroundColor={ChipBackgroundColor.Red}
        >
          {t('ImpersonateUserDialog.EmployeeImpersonated', {
            employeeId: this.props.employee.employee_external_id
          })}
        </Chip>
      );
    }
  }

  // This is a way to get focus set on the title since React Router doesn't support setting focus on
  // navigation. When the user releases the mouse click or releases "ENTER", we set focus to the title
  // and then navigate to the dashboard. If we are on the dashboard then we just focus on the title.
  focusOnPageTitle = (event) => {
    if (event.type === "mouseup" || event.keyCode === 13) {
      setFocusToDashboard();
    }
  }

  getLogoWithPageHeaderLink = () => {
    return (
      <PageHeaderRouterLink
        dataTestId="HeaderLogo-PageHeaderRouterLink"
        to={{pathname: NavigationTo.AtGlance, state: {fromLogo: true}}}
        aria-current={null}
        hasHover={false}
        onMouseUp={this.focusOnPageTitle}
        onKeyUp={this.focusOnPageTitle}
      >
        <Logo/>
      </PageHeaderRouterLink>
    );
  }

  getUserMenuItems = (t, userName, employeeName=null) => {
    let menuItems = [];

    menuItems.push(
      <View
        backgroundColor="neutral10"
        padding={15}
        style={{borderRadius: 5}}
      >
        <Avatar
          username={userName}
          fullName={employeeName}
          showUsername={false}
          showImage={false}
        >
          <AvatarText hiddenText="Employee ID">
            <Text fontSize="T200">
              {t('ImpersonateUserDialog.EmployeeIdLabel')}: {this.props.employee.employee_external_id}
            </Text>
          </AvatarText>
        </Avatar>
      </View>
    )

    if (this.isImpersonated()) {
      menuItems.push(
        <PageHeaderDropdownButton
          dataTestId="StopImpersonation-DropdownButton"
          onClick={() => this.stopImpersonation()}
          icon={<IconPadlock aria-hidden={true}/>}>
          {t('Navigation.StopImpersonation')}
        </PageHeaderDropdownButton>
      )
    } else {
      menuItems.push(
        <PageHeaderDropdownButton
          dataTestId="ChangeLanguage-DropdownButton"
          onClick={() => this.setChangeLanguageDialogVisble(true)}
          icon={<IconGlobe aria-hidden={true}/>}>
          {t('Navigation.ChangeLanguage')}
        </PageHeaderDropdownButton>
      );
    }

    if (this.canImpersonate()) {
      menuItems.push(
        <PageHeaderDropdownButton
          dataTestId="ImpersonateUser-DropdownButton"
          onClick={() => this.setImpersonateUserDialogVisible(true)}
          icon={<IconPadlock aria-hidden={true}/>}>
          {t('Navigation.ImpersonateUser')}
        </PageHeaderDropdownButton>
      )
    }

    return menuItems;
  }

  getUserName = (employee) => {
    if (employeeService.isAPSEmployee(employee) && employee.email) {
      return employee.email.split("@")[0];
    }
    return NO_EXISTING_USER_NAME;
  }

  renderLogoSide = (t) => {
    return (
      <WithBreakpoints>
        {({matches}) => (
          <>
            {matches.s &&
              <>
                <Col justifyContent="space-evenly">
                  {this.getLogoWithPageHeaderLink()}
                  {this.createImpersonatingIndicator(t)}
                </Col>
                <Spacer flex={1}/>
              </>
            }

            {!matches.s &&
              <>
                <Row alignItems="center">
                  {this.getLogoWithPageHeaderLink()}
                  {this.createImpersonatingIndicator(t)}
                </Row>
                <Spacer flex={2}/>
              </>
            }
          </>
        )}
      </WithBreakpoints>
    )
  }

  renderUserMenuSide = (t, employee) => {
    const userName = this.getUserName(employee);
    const employeeId = this.props.employee.employee_external_id;
    const firstName = employee.preferred_first_name && employee.preferred_first_name.length > 0 ? employee.preferred_first_name : employee.first_name
    const lastName = employee.preferred_last_name && employee.preferred_last_name.length > 0 ? employee.preferred_last_name : employee.last_name
    const employeeName = firstName + " " + lastName;

    return (
      <nav aria-label="Site" style={{display: 'flex'}}>
        <WithBreakpoints>
          {({matches}) => (
            <>
              {matches.s &&
                <PageHeaderDropdown
                  dataTestId="HeaderMenu-PageHeaderDropdown"
                  items={this.getUserMenuItems(t, userName, employeeName)}
                  isSelected={false}
                >
                  <Avatar
                    username={userName}
                    employeeId={employeeId}
                    showUsername={false}
                    colorScheme={AvatarColorScheme.Inverted}
                  />
                </PageHeaderDropdown>
              }

              {!matches.s &&
                <PageHeaderDropdown
                  dataTestId="HeaderMenu-PageHeaderDropdown"
                  items={this.getUserMenuItems(t, userName)}
                  isSelected={false}
                >
                  <Avatar
                    username={userName}
                    employeeId={employeeId}
                    fullName={employeeName}
                    showUsername={false}
                    colorScheme={AvatarColorScheme.Inverted}
                  />
                </PageHeaderDropdown>
              }
            </>
          )}
        </WithBreakpoints>
      </nav>
    )
  }

  render() {
    const {t, employee} = this.props;

    return (
      <PageHeader
        isFullWidth={true}
        isFixed={true}
        aria-label="Page header"
      >
        {this.renderLogoSide(t)}
        {this.renderUserMenuSide(t, employee)}

        <ChangeLanguageDialog
          dataTestId="ChangeLanguageDialog"
          visible={this.state.isChangeLanguageDialogVisible}
          onCloseDialog={() => this.setChangeLanguageDialogVisble(false)}
        />
        <ImpersonateUserDialog
          dataTestId="ImpersonateUserDialog"
          visible={this.state.isImpersonateUserDialogVisible}
          onCloseDialog={() => this.setImpersonateUserDialogVisible(false)}
        />
      </PageHeader>
    )
  }
}

const mapState = state => ({
  employee: state.employee,
  languageName: employeeService.getLanguageByCode(state.configuration.languages, state.employee.language).name
});

const mapDispatchToProps = dispatch => ({
  actions: {
    authentication: bindActionCreators(authenticationActions, dispatch),
    employee: bindActionCreators(employeeActions, dispatch),
    staticContent: bindActionCreators(staticContentActions, dispatch),
    configuration: bindActionCreators(configurationActions, dispatch),
  }
});

export default connect(mapState, mapDispatchToProps)(withTranslation('translationsV2')(UserMenu))
